import type { CSSProperties, ReactNode } from "react";
import MuiBadge from "@mui/material/Badge";
import { useTheme } from "@mui/material/styles";

// antd <Badge> -> MUI Badge. count->badgeContent, dot->variant="dot",
// overflowCount->max, status/color kept for compat.
type BadgeColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

export interface BadgeProps {
  count?: ReactNode;
  dot?: boolean;
  overflowCount?: number;
  showZero?: boolean;
  status?: "success" | "processing" | "default" | "error" | "warning";
  color?: BadgeColor | string;
  offset?: [number, number];
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}

const STATUS_COLOR: Record<string, BadgeColor> = {
  success: "success", processing: "info", error: "error", warning: "warning", default: "default",
};

export const Badge = ({ count, dot, overflowCount, showZero, status, color, children, style, className }: BadgeProps) => {
  const theme = useTheme();
  // Default badge fill = the theme error colour (antd's default badge was red);
  // keeps the count/dot consistent with the app's semantic palette.
  const defaultBg = theme.palette.error.main;
  const paletteColor: BadgeColor =
    (status && STATUS_COLOR[status]) ||
    (color && ["primary", "secondary", "error", "info", "success", "warning", "default"].includes(color) ? (color as BadgeColor) : "error");

  // antd allows a STANDALONE badge (no children): a small pill/dot rendered inline
  // (e.g. the notes-category count). MUI Badge must anchor to a child — with no
  // children it collapses onto a zero-size box and overlaps its neighbour, and a
  // caller's `style` lands on the (invisible) root instead of the badge, so a custom
  // backgroundColor is lost and the count falls back to error-red. Render the
  // standalone form ourselves for parity. `style` wins (spread last), matching antd.
  if (children == null) {
    const customColor = typeof color === "string" && color.startsWith("#") ? color : undefined;
    if (dot) {
      return (
        <span
          className={className}
          style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", backgroundColor: customColor ?? defaultBg, ...style }}
        />
      );
    }
    const n = typeof count === "number" ? count : undefined;
    if (count == null || (n === 0 && !showZero)) return null;
    const text = n !== undefined && overflowCount && n > overflowCount ? `${overflowCount}+` : count;
    return (
      <span
        className={className}
        style={{
          display: "inline-block", minWidth: 20, height: 20, padding: "0 6px",
          borderRadius: 10, backgroundColor: customColor ?? defaultBg, color: "#fff",
          fontSize: 12, lineHeight: "20px", textAlign: "center", whiteSpace: "nowrap",
          boxSizing: "border-box", ...style,
        }}
      >
        {text}
      </span>
    );
  }

  return (
    <MuiBadge
      badgeContent={dot ? undefined : count}
      variant={dot ? "dot" : "standard"}
      max={overflowCount}
      showZero={showZero}
      color={paletteColor}
      style={style}
      className={className}
    >
      {children}
    </MuiBadge>
  );
};

export default Badge;
